import React, { useRef, useState } from 'react';
import moment from 'moment';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { SubmissionCommentSection } from 'components/organisms/Task/TaskSection';
import { useAuth } from 'contexts/auth';
import feedbackService from 'services/feedback';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AlertDialog,
    Divider,
    FeedbackTile,
    ResourceChip,
    Stack,
    Text,
} from 'shell/components';
import { SUBMISSION_TYPES, TYPES } from 'utils/constants';
import { getUserPreferredFullName } from 'utils/helper';

const SubmissionView = ({ submissionsState, setSubmissionsState, getUserByAccountId }) => {
    const { mode, submissions } = submissionsState;

    const { auth } = useAuth();

    const commentInputRef = useRef(null);

    const [confirmationModal, setConfirmationModal] = useState({
        visible: false,
        destructive: false,
        title: '',
        content: '',
        confirmText: 'Submit',
        onConfirm: () => {},
        onCancel: () => {},
    });
    const [currentEditingComment, setCurrentEditingComment] = useState(null);

    const closeConfirmationModal = () => {
        setConfirmationModal((current) => ({
            ...current,
            visible: false,
            onConfirm: () => {},
            onCancel: () => {},
        }));
    };

    const handleAddComment = (html) => {
        const now = new Date().getTime();
        const newComment = {
            id: uuid.v4(),
            active: true,
            created_on: now,
            modified_on: now,
            owner_account_id: auth.owner_account_id,
            course_id: submissions[0].course_id,
            group_id: submissions[0].group_id,
            text: html.trim(),
            type: TYPES.SUBMISSION,
            submission_id: submissions[0].id,
        };
        setSubmissionsState((current) => {
            return {
                ...current,
                submissions: current.submissions.map((submission, index) => {
                    if (index !== 0) return submission;
                    return {
                        ...submission,
                        comments: [...submission.comments, newComment],
                    };
                }),
            };
        });
        feedbackService.createFeedback(newComment);
    };

    const handleEditComment = (html) => {
        const updatedComment = {
            ...currentEditingComment,
            modified_on: new Date().getTime(),
            text: html.trim(),
        };
        setSubmissionsState((current) => {
            return {
                ...current,
                submissions: current.submissions.map((submission, index) => {
                    if (index !== 0) return submission;
                    return {
                        ...submission,
                        comments: submission.comments.map((comment) => {
                            return comment.id === currentEditingComment.id ? updatedComment : comment;
                        }),
                    };
                }),
            };
        });
        feedbackService.updateFeedback(updatedComment.id, updatedComment);
        setCurrentEditingComment(null);
    };

    const handleSelect = (value, comment) => {
        if (value === 'edit') {
            setCurrentEditingComment(comment);
            setTimeout(() => {
                commentInputRef.current.props.editor.commands.focus();
            }, 100);
        }
        if (value === 'delete') {
            setConfirmationModal((current) => ({
                ...current,
                visible: true,
                destructive: true,
                title: 'Delete Comment?',
                content: 'Are you sure you want to permanently delete this comment?',
                confirmText: 'Delete',
                onConfirm: () => {
                    setSubmissionsState((current) => {
                        return {
                            ...current,
                            submissions: current.submissions.map((submission, index) => {
                                if (index !== 0) return submission;
                                return {
                                    ...submission,
                                    comments: submission.comments.filter(({ id }) => id !== comment.id),
                                };
                            }),
                        };
                    });
                    closeConfirmationModal();
                    feedbackService.deleteFeedback(comment.id);
                },
                onCancel: closeConfirmationModal,
            }));
        }
    };

    const renderSubmissionItems = (submission) => {
        return (
            <ResourceChip
                resource={
                    submission.submission_type === SUBMISSION_TYPES.FILE ? submission.files[0] : { ...submission.url }
                }
                size={'medium'}
                type={submission.submission_type === SUBMISSION_TYPES.FILE ? 'file' : 'link'}
            />
        );
    };

    return (
        <>
            <Stack spacing={2}>
                <Text variant={'p2'} weight={'semibold'}>
                    {mode === 'history' ? 'Submission History' : 'Submission Details'}
                </Text>
                {mode === 'history' ? (
                    <Stack spacing={2}>
                        {submissions.map((submission, index) => (
                            <CustomStack key={submission.id} index={index} spacing={2}>
                                {[SUBMISSION_TYPES.FILE, SUBMISSION_TYPES.URL].includes(submission.submission_type) &&
                                    renderSubmissionItems(submission)}
                                <Text color={'light'} variant={'p3'}>
                                    Submitted on {moment(submission.created_on).format('MMM D [@] h:mma')} by{' '}
                                    {getUserPreferredFullName(getUserByAccountId(submission.owner_account_id))}
                                </Text>
                                <CustomAccordion defaultExpanded={Boolean(index === 0 && submission.comments.length)}>
                                    <AccordionSummary>
                                        <Text variant={'p2'}>Comments ({submission.comments.length})</Text>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            {submission.comments.map((comment) => (
                                                <FeedbackTile
                                                    key={comment.id}
                                                    author={getUserByAccountId(comment.owner_account_id)}
                                                    feedback={comment}
                                                    getNameById={(id) =>
                                                        getUserPreferredFullName(getUserByAccountId(id))
                                                    }
                                                    options={
                                                        comment.owner_account_id === auth.owner_account_id
                                                            ? [{ key: 'delete', value: 'Delete' }]
                                                            : []
                                                    }
                                                    onSelect={({ key }) => handleSelect(key, comment)}
                                                />
                                            ))}
                                        </Stack>
                                    </AccordionDetails>
                                </CustomAccordion>
                            </CustomStack>
                        ))}
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        <Text color={'light'} variant={'p3'}>
                            Submitted on {moment(submissions[0].created_on).format('MMM D [@] h:mma')} by{' '}
                            {getUserPreferredFullName(getUserByAccountId(submissions[0].owner_account_id))}
                        </Text>
                        <Text rich variant={'p2'}>
                            {submissions[0].plaintext}
                        </Text>
                        <Divider />
                        <SubmissionCommentSection
                            ref={commentInputRef}
                            submissions={submissions}
                            getUserByAccountId={getUserByAccountId}
                            handleSelect={handleSelect}
                            currentEditingComment={currentEditingComment}
                            setCurrentEditingComment={setCurrentEditingComment}
                            handleAddComment={handleAddComment}
                            handleEditComment={handleEditComment}
                        />
                    </Stack>
                )}
            </Stack>
            <AlertDialog
                open={confirmationModal.visible}
                variant={confirmationModal.destructive ? 'destructive' : 'primary'}
                message={confirmationModal.title}
                description={confirmationModal.content}
                actionButtonText={confirmationModal.confirmText}
                onAction={confirmationModal.onConfirm}
                onCancel={confirmationModal.onCancel}
                onClose={confirmationModal.onCancel}
            />
        </>
    );
};

const CustomStack = styled(Stack)(({ index, theme }) => {
    return {
        margin: theme.spacing(0, -5),
        padding: theme.spacing(5),
        ...(index % 2 === 0 && {
            background: theme.palette.ensightful.neutral[50],
        }),
    };
});

const CustomAccordion = styled(Accordion)(() => {
    return {
        background: 'inherit',
    };
});

export default SubmissionView;
